/**
 * StorageToastSystem — controller for V2 canvas storage events.
 *
 * Cycle 101 sprint-3 Task 3.9 (initial), migrated to the shared `Toast`
 * primitive in cycle 101.1 sprint-3 Task 3.6 (beads `26gu`).
 *
 * Listens to the three CustomEvents emitted by `useModeCanvasLayout`:
 *   * `echelon.frontend.storage_quota_exceeded` → persistent toast:
 *     "Layout changes won't persist — clear browser storage"
 *   * `echelon.frontend.layout_cache_bust` → dismissable toast with
 *     RESTORE button: "Your {MODE} layout was reset — RESTORE?"
 *     The RESTORE button reads from `recycle.{mode}` storage key and
 *     writes back to `layout.v5.{mode}` (reversing the cache-bust move).
 *   * `echelon.frontend.layout_parse_failure` / `layout_zod_failure` /
 *     `layout_mode_mismatch` → Sentry breadcrumb (or console.warn
 *     fallback) with payload digest only. No user-facing toast.
 *
 * This component renders nothing — it pushes toasts into the shared
 * stack via `pushToast`. Mount `<ToastStack />` once at the V2 shell to
 * see them.
 */

import { useEffect } from 'react';
import { pushToast } from '../common/Toast';
import {
  layoutStorageKey,
  recycleStorageKey,
} from '../../hooks/useModeCanvasLayout';
import { PRESET_VERSION, type ModeId } from '../../lib/modePresets';

// ── Event detail shapes (mirror emitters in useModeCanvasLayout.ts) ─────

interface QuotaEventDetail {
  readonly mode: ModeId;
  readonly error: string;
}
interface CacheBustEventDetail {
  readonly mode: ModeId;
  readonly preset_version_before: number;
  readonly preset_version_after: number;
}
interface FailureEventDetail {
  readonly mode: ModeId;
  readonly digest: string;
}

// ── Sentry shim (optional) ──────────────────────────────────────────────

interface SentryBreadcrumbPayload {
  readonly category: string;
  readonly message: string;
  readonly level?: 'info' | 'warning' | 'error';
  readonly data?: Record<string, unknown>;
}

interface SentryLike {
  addBreadcrumb?: (payload: SentryBreadcrumbPayload) => void;
}

function getSentry(): SentryLike | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { Sentry?: SentryLike };
  return w.Sentry ?? null;
}

export function reportBreadcrumb(payload: SentryBreadcrumbPayload): void {
  const sentry = getSentry();
  if (sentry && typeof sentry.addBreadcrumb === 'function') {
    sentry.addBreadcrumb(payload);
    return;
  }
  // Fallback: console.warn with grep-friendly prefix.
  // eslint-disable-next-line no-console
  console.warn('[sentry-breadcrumb]', payload.category, payload.message, payload.data ?? {});
}

// ── RESTORE-from-recycle handler ────────────────────────────────────────

/** Move `recycle.{mode}` → `layout.v5.{mode}`. Returns true on success.
 *
 * DISS-001-R2 fix: stamp the restored payload with the current
 * `PRESET_VERSION` (and `updatedAt`) before writing. Without this, the
 * recycled payload still carries the OLD `presetVersion`, so the next
 * mount's hydration immediately cache-busts the restored layout back to
 * the recycle tier — an infinite RESTORE loop. The bump accepts user
 * customizations from a previous preset version as-is; if the preset
 * schema removed modules, the canvas shows placeholders for unknown
 * `moduleId`s (best-effort recovery; acceptable for a user-initiated
 * RESTORE action). */
export function restoreFromRecycle(mode: ModeId): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(recycleStorageKey(mode));
    if (raw === null) return false;

    let payloadToWrite = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const restamped = {
          ...(parsed as Record<string, unknown>),
          presetVersion: PRESET_VERSION,
          updatedAt: new Date().toISOString(),
        };
        payloadToWrite = JSON.stringify(restamped);
      }
    } catch {
      // Invalid JSON — fall through. Hydration will Zod-fail and fall back
      // to preset; better than blocking recovery.
    }

    window.localStorage.setItem(layoutStorageKey(mode), payloadToWrite);
    window.localStorage.removeItem(recycleStorageKey(mode));
    return true;
  } catch {
    return false;
  }
}

// ── Component (controller — renders nothing) ────────────────────────────

export function StorageToastSystem(): null {
  useEffect(() => {
    function onQuota(e: Event) {
      const detail = (e as CustomEvent<QuotaEventDetail>).detail;
      pushToast({
        message: `Layout changes won't persist — clear browser storage. (${detail.error})`,
        variant: 'error',
        durationMs: null, // persistent — operator must clear storage
        dedupeKey: `quota-${detail.mode}`,
        dataTestId: 'toast-quota',
      });
    }

    function onCacheBust(e: Event) {
      const detail = (e as CustomEvent<CacheBustEventDetail>).detail;
      pushToast({
        message: `Your ${detail.mode.toUpperCase()} layout was reset — RESTORE?`,
        variant: 'info',
        durationMs: null,
        dedupeKey: `cache-bust-${detail.mode}`,
        dataTestId: 'toast-cache-bust',
        action: {
          label: 'Restore',
          onAction: () => {
            const ok = restoreFromRecycle(detail.mode);
            if (ok) {
              try {
                window.location.reload();
              } catch {
                // noop in jsdom / tests
              }
            }
          },
        },
      });
      reportBreadcrumb({
        category: 'storage.cache_bust',
        message: 'Mode preset-version drift: layout moved to recycle tier',
        level: 'info',
        data: {
          mode: detail.mode,
          preset_version_before: detail.preset_version_before,
          preset_version_after: detail.preset_version_after,
        },
      });
    }

    function onParseFailure(e: Event) {
      const detail = (e as CustomEvent<FailureEventDetail>).detail;
      reportBreadcrumb({
        category: 'storage.layout_parse_failure',
        message: 'Layout JSON parse failure — preset fallback applied',
        level: 'warning',
        data: { mode: detail.mode, digest: detail.digest },
      });
    }

    function onZodFailure(e: Event) {
      const detail = (e as CustomEvent<FailureEventDetail>).detail;
      reportBreadcrumb({
        category: 'storage.layout_zod_failure',
        message: 'Layout Zod validation failure — preset fallback applied',
        level: 'warning',
        data: { mode: detail.mode, digest: detail.digest },
      });
    }

    function onModeMismatch(e: Event) {
      const detail = (e as CustomEvent<FailureEventDetail>).detail;
      reportBreadcrumb({
        category: 'storage.layout_mode_mismatch',
        message: 'Stored mode does not match storage key — preset fallback',
        level: 'warning',
        data: { mode: detail.mode, digest: detail.digest },
      });
    }

    window.addEventListener('echelon.frontend.storage_quota_exceeded', onQuota);
    window.addEventListener('echelon.frontend.layout_cache_bust', onCacheBust);
    window.addEventListener('echelon.frontend.layout_parse_failure', onParseFailure);
    window.addEventListener('echelon.frontend.layout_zod_failure', onZodFailure);
    window.addEventListener('echelon.frontend.layout_mode_mismatch', onModeMismatch);

    return () => {
      window.removeEventListener('echelon.frontend.storage_quota_exceeded', onQuota);
      window.removeEventListener('echelon.frontend.layout_cache_bust', onCacheBust);
      window.removeEventListener('echelon.frontend.layout_parse_failure', onParseFailure);
      window.removeEventListener('echelon.frontend.layout_zod_failure', onZodFailure);
      window.removeEventListener('echelon.frontend.layout_mode_mismatch', onModeMismatch);
    };
  }, []);

  return null;
}
